// ===== AUTO-GROUP JOBS =====
// Given unassigned jobs for a date plus available employees, build route
// suggestions that respect geographic proximity, the day's timeline, and
// a revenue-per-person target (default $300/person/day).
//
// This is a pure utility — it returns suggestions only. The caller decides
// whether to apply them by writing routeId/team/teamNames to each job.

import { OFFICE } from './jobData';

const REVENUE_PER_PERSON_DEFAULT = 300;
const MAX_DAY_MINUTES = 9 * 60;        // 9 hour day cap incl. drive
const AVG_DRIVE_BETWEEN_MINUTES = 12;  // rough estimate when we don't have a Maps result
const MILES_PER_DEGREE = 69;           // rough lat conversion

// Haversine-ish quick miles distance between two lat/lng points
function quickMiles(a, b) {
  if (!a || !b || a.lat == null || b.lat == null) return 0;
  const dLat = (a.lat - b.lat) * MILES_PER_DEGREE;
  const dLng = (a.lng - b.lng) * MILES_PER_DEGREE * Math.cos((a.lat * Math.PI) / 180);
  return Math.sqrt(dLat * dLat + dLng * dLng);
}

// Convert miles to drive minutes (very rough: 30 mph average)
const milesToMinutes = (mi) => Math.max(5, Math.round(mi * 2));

// Parse "HH:MM" -> minutes
function parseTime(t) {
  if (!t) return null;
  const [h, m] = t.split(':').map(Number);
  return h * 60 + (m || 0);
}

function durationMinutes(job) {
  if (job.duration) return Math.round(job.duration * 60);
  if (job.scheduledTime && job.scheduledEndTime) {
    const a = parseTime(job.scheduledTime);
    const b = parseTime(job.scheduledEndTime);
    if (a != null && b != null) return Math.max(60, b - a);
  }
  return 150; // default 2.5h
}

// Cluster unassigned jobs into geographic groups using a simple greedy
// nearest-neighbor sweep starting from the job closest to the office.
function clusterByProximity(jobs, maxClusterMiles = 8) {
  if (jobs.length === 0) return [];
  const remaining = [...jobs];
  const clusters = [];

  while (remaining.length > 0) {
    // Seed each cluster with the job closest to the office
    remaining.sort((a, b) => quickMiles(a, OFFICE) - quickMiles(b, OFFICE));
    const seed = remaining.shift();
    const cluster = [seed];

    let grew = true;
    while (grew) {
      grew = false;
      let bestIdx = -1;
      let bestDist = Infinity;
      for (let i = 0; i < remaining.length; i++) {
        // Distance from candidate to nearest job in cluster
        const d = Math.min(...cluster.map(c => quickMiles(remaining[i], c)));
        if (d < bestDist && d <= maxClusterMiles) {
          bestDist = d;
          bestIdx = i;
        }
      }
      if (bestIdx >= 0) {
        cluster.push(remaining.splice(bestIdx, 1)[0]);
        grew = true;
      }
    }
    clusters.push(cluster);
  }
  return clusters;
}

// Order jobs within a cluster by scheduled time first, then by proximity
// (nearest-neighbor from the office) for any without a scheduled time.
function orderClusterByTimeline(cluster) {
  const withTime = cluster.filter(j => j.scheduledTime).sort(
    (a, b) => parseTime(a.scheduledTime) - parseTime(b.scheduledTime)
  );
  const noTime = cluster.filter(j => !j.scheduledTime);

  // Insert unscheduled jobs at nearest-neighbor positions
  const ordered = [...withTime];
  while (noTime.length > 0) {
    const ref = ordered.length > 0 ? ordered[ordered.length - 1] : OFFICE;
    noTime.sort((a, b) => quickMiles(a, ref) - quickMiles(b, ref));
    ordered.push(noTime.shift());
  }
  return ordered;
}

// Validate that an ordered cluster fits within a working day timeline.
// Splits into multiple sub-routes if total time exceeds the day cap.
function splitByTimeline(orderedJobs) {
  const routes = [];
  let current = [];
  let currentMinutes = AVG_DRIVE_BETWEEN_MINUTES; // initial office-to-first

  for (const job of orderedJobs) {
    const jobMin = durationMinutes(job);
    const driveMin = current.length > 0
      ? milesToMinutes(quickMiles(current[current.length - 1], job))
      : AVG_DRIVE_BETWEEN_MINUTES;

    if (currentMinutes + driveMin + jobMin > MAX_DAY_MINUTES && current.length > 0) {
      routes.push(current);
      current = [job];
      currentMinutes = AVG_DRIVE_BETWEEN_MINUTES + jobMin;
    } else {
      current.push(job);
      currentMinutes += driveMin + jobMin;
    }
  }
  if (current.length > 0) routes.push(current);
  return routes;
}

// Decide how many people to assign based on total route revenue and the
// $300/person/day target. Always at least 1, capped to a sensible max.
function recommendTeamSize(routeRevenue, targetPerPerson) {
  const ideal = Math.max(1, Math.round(routeRevenue / targetPerPerson));
  return Math.min(4, ideal);
}

// Estimate hours already booked per employee for the date (only counts
// jobs the employee is on the team for).
function buildEmployeeLoad(allJobsForDate) {
  const load = {}; // empId -> { hours, jobs }
  for (const job of allJobsForDate) {
    if (!job.team || job.team.length === 0) continue;
    for (const empId of job.team) {
      if (!load[empId]) load[empId] = { hours: 0, jobs: 0 };
      load[empId].hours += job.duration || 0;
      load[empId].jobs += 1;
    }
  }
  return load;
}

// Score an employee for a route: prefer people who are lightly loaded.
// We don't try to be too clever about skills here; that can be added later.
function scoreEmployee(emp, load) {
  const empLoad = load[emp.id]?.hours || 0;
  // Lower load = higher score. Add small jitter by name to keep things stable.
  return -empLoad;
}

// Choose a team of `size` employees from `availableEmployees`, preferring
// those with the lightest existing load for the day.
function pickTeam(size, availableEmployees, load) {
  const ranked = [...availableEmployees].sort((a, b) => scoreEmployee(b, load) - scoreEmployee(a, load));
  return ranked.slice(0, size);
}

/**
 * Build route suggestions for a day.
 *
 * @param {Object} opts
 * @param {Array}  opts.unassignedJobs   Jobs with no team for the target date.
 * @param {Array}  opts.allJobsForDate   All jobs on the date (used to estimate employee load).
 * @param {Array}  opts.employees        Active employees available for the day.
 * @param {Array}  [opts.unavailableEmployeeIds=[]] IDs to exclude (time off, etc).
 * @param {number} [opts.targetPerPerson=300]
 * @returns {Array<{
 *    jobs: Array,
 *    revenue: number,
 *    perPerson: number,
 *    teamSize: number,
 *    team: Array,        // employee objects
 *    teamIds: Array,
 *    teamNames: Array,
 *    routeName: string,
 *    estimatedMinutes: number
 * }>}
 */
export function buildAutoGroupSuggestions({
  unassignedJobs = [],
  allJobsForDate = [],
  employees = [],
  unavailableEmployeeIds = [],
  targetPerPerson = REVENUE_PER_PERSON_DEFAULT,
} = {}) {
  if (unassignedJobs.length === 0) return [];

  const available = employees.filter(
    e => e.status === 'active' && !unavailableEmployeeIds.includes(e.id)
  );
  if (available.length === 0) return [];

  const load = buildEmployeeLoad(allJobsForDate);

  const clusters = clusterByProximity(unassignedJobs);
  const suggestions = [];
  const usedEmpIds = new Set();

  for (const cluster of clusters) {
    const ordered = orderClusterByTimeline(cluster);
    const dayRoutes = splitByTimeline(ordered);

    for (const routeJobs of dayRoutes) {
      const revenue = routeJobs.reduce((s, j) => s + (j.price || 0), 0);
      const teamSize = recommendTeamSize(revenue, targetPerPerson);

      // Pick employees not already used by an earlier suggestion in this pass
      const pool = available.filter(e => !usedEmpIds.has(e.id));
      const team = pickTeam(teamSize, pool.length >= teamSize ? pool : available, load);
      team.forEach(e => usedEmpIds.add(e.id));

      const estimatedMinutes = routeJobs.reduce(
        (s, j, i) => s + durationMinutes(j) +
          (i === 0 ? AVG_DRIVE_BETWEEN_MINUTES
                   : milesToMinutes(quickMiles(routeJobs[i - 1], j))),
        0
      );

      const teamIds = team.map(t => t.id);
      const teamNames = team.map(t => `${t.firstName} ${t.lastName?.[0] || ''}.`);
      const sorted = [...teamIds].sort();

      suggestions.push({
        jobs: routeJobs,
        revenue: Math.round(revenue * 100) / 100,
        perPerson: team.length > 0 ? Math.round((revenue / team.length) * 100) / 100 : 0,
        teamSize: team.length,
        team,
        teamIds,
        teamNames,
        routeId: sorted.length > 0 ? `team_${sorted.join('_')}` : null,
        routeName: teamNames.map(n => n.split(' ')[0]).filter(Boolean).join(' & ') || `Route ${suggestions.length + 1}`,
        estimatedMinutes,
      });
    }
  }
  return suggestions;
}

export const AUTO_GROUP_DEFAULTS = {
  targetPerPerson: REVENUE_PER_PERSON_DEFAULT,
  maxClusterMiles: 8,
  maxDayMinutes: MAX_DAY_MINUTES,
};
